// src/pages/payments/PaymentReturn.jsx
// FASE 3.B + F5 — Pantalla "urlReturn" a la que Flow.cl redirige tras pago/registro.
//
// Maneja DOS flujos:
//
// 1) SUBSCRIPTION (F5): user volvió de registrar tarjeta para suscripción.
//    Backend persiste users/{uid}.pendingSubscription.{registerToken, planId, flowPlanId}
//    en flowCreatePayment cuando arranca el flujo. Acá leemos eso, invocamos
//    flowCardRegistered(token) callable que verifica con Flow + crea subscription.
//    Después backend setea flowSubscriptionId + borra pendingSubscription.
//
// 2) LEGACY ONE-SHOT (test_1000 admin / pagos viejos): no hay pendingSubscription.
//    Comportamiento original: escucha último payment del uid en payments collection
//    y redirige a /pago/exito o /pago/error según data.status.
//
// Detección: lee user doc UNA vez al cargar. Si pendingSubscription.registerToken
// existe → flujo subscription. Si flowSubscriptionId ya está + status TRIAL/PAID
// → success directo (caso edge: ya activó). Sino → flujo legacy.
//
// Flow.cl envía urlReturn vía POST con token en body para /payment, NO query GET.
// Para /customer/register es similar — por eso NO leemos token de la URL,
// usamos el persistido en pendingSubscription.

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';

// F5 hotfix 2026-05-10: query params del flowSubscribeCallback
//   ?activated=true              → success path post-form.submit
//   ?error=registration_failed   → tarjeta rechazada
//   ?error=token_not_found       → token invalido (race condition)
//   ?error=subscription_failed   → getRegisterStatus OK pero createSubscription fallo
const SUBSCRIPTION_ERROR_MESSAGES = {
  registration_failed: 'Tu tarjeta no fue aceptada por el banco. Prueba con otra.',
  token_not_found: 'La sesión de registro expiró. Vuelve a empezar desde el paywall.',
  subscription_failed: 'La tarjeta se registró pero la suscripción no se activó. Contacta a soporte.',
  subscription_no_id: 'La suscripción no se pudo crear. Contacta a soporte.',
  register_status_failed: 'No pudimos verificar tu tarjeta. Prueba de nuevo en un momento.',
  missing_token: 'Falta información del registro. Vuelve a empezar.',
  lookup_failed: 'Error técnico interno. Prueba de nuevo.',
  // F5 FASE 2 anti-abuso: tarjeta o customer en blockedTrials.
  card_blocked: 'Esta tarjeta ya fue usada en otra cuenta que tuvo un cobro fallido. Para suscribirte, puedes usar otra tarjeta o contactar soporte@edgeelite.cl. ✓ Tu tarjeta NO fue cobrada.',
};

const TIMEOUT_MS = 30_000;

export default function PaymentReturn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [debugMsg, setDebugMsg] = useState('Verificando pago…');
  const [error, setError] = useState(null);
  const invokedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setDebugMsg('Sesión expirada. Redirigiendo a login…');
      const t = setTimeout(() => navigate('/login'), 1500);
      return () => clearTimeout(t);
    }

    let unsubLegacy = null;
    let timeoutId = null;
    let cancelled = false;

    // F5 hotfix 2026-05-10: detectar query params del flowSubscribeCallback.
    // Camino feliz: ?activated=true → user doc ya tiene flowSubscriptionId
    // (el callback HTTP lo persistió antes del redirect 302).
    // Error path: ?error=<reason> → mostrar mensaje específico + retry.
    const search = new URLSearchParams(location.search || '');
    const activated = search.get('activated');
    const errorReason = search.get('error');

    if (errorReason) {
      const msg = SUBSCRIPTION_ERROR_MESSAGES[errorReason]
        || 'Hubo un problema con tu suscripción. Prueba de nuevo.';
      setError(msg);
      return;
    }

    // FIX 2: listener del flujo one-shot (Webpay/Khipu/Mach). Escucha el último
    // payment del uid y rutea según status. flowConfirm (sandbox-aware) actualiza
    // ese doc a 'paid'/'rejected' tras verificar con Flow.
    function startPaymentsListener() {
      const q = query(
        collection(firestore, 'payments'),
        where('uid', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(1),
      );

      unsubLegacy = onSnapshot(
        q,
        (snap) => {
          if (cancelled) return;
          if (snap.empty) return;
          const data = snap.docs[0].data();
          if (data.status === 'paid') {
            navigate('/pago/exito');
          } else if (data.status === 'rejected') {
            navigate('/pago/error');
          }
          // pending → seguimos escuchando
        },
        (err) => {
          if (cancelled) return;
          console.error('[PaymentReturn] snapshot error:', err);
          setDebugMsg('Error verificando pago.');
        },
      );

      timeoutId = setTimeout(() => {
        if (!cancelled) navigate('/pago/pendiente');
      }, TIMEOUT_MS);
    }

    async function init() {
      // FIX 2: pago único (one-shot) → el Checkout dejó esta señal en localStorage
      // antes de redirigir a Flow. Detectarla ANTES de las ramas de suscripción
      // (que se disparan con pendingSubscription) y rutear por el listener de
      // payments. Sin esto, un pendingSubscription residual mandaba el one-shot
      // a la pantalla de error de suscripción.
      const isOneShot = localStorage.getItem('ee_processing_next') === 'payment_success';
      if (isOneShot) {
        localStorage.removeItem('ee_processing_next');
        setDebugMsg('Confirmando tu pago…');
        startPaymentsListener();
        return;
      }

      const userRef = doc(firestore, 'users', user.uid);
      let userSnap;
      try {
        userSnap = await getDoc(userRef);
      } catch (e) {
        console.error('[PaymentReturn] getDoc error:', e);
        setError('No pudimos leer tu cuenta. Prueba a refrescar.');
        return;
      }
      const u = (userSnap.exists() && userSnap.data()) || {};

      // F5 path: ?activated=true + flowSubscriptionId → success.
      // Routing post-success: si user no tiene Telegram bound,
      // redirigir a /conectar-telegram. Sino, /dashboard.
      // El campo real es telegramChatId (no telegramBound), consistente
      // con MiCuentaModal y el resto del frontend.
      function nextRouteAfterSuccess(userData) {
        const isTgBound = Boolean(userData && userData.telegramChatId);
        return isTgBound ? '/dashboard' : '/conectar-telegram';
      }

      if (activated === 'true') {
        if (u.flowSubscriptionId && (u.subscription_status === 'TRIAL' || u.subscription_status === 'PAID')) {
          // Trial con 5 días gratis → pantalla TrialActive. Suscripción sin
          // trial (PAID: legacy/bloqueado) → routing normal (telegram/dashboard).
          const next = u.subscription_status === 'TRIAL' ? '/trial-activo' : nextRouteAfterSuccess(u);
          setDebugMsg('¡Suscripción activa!');
          const t = setTimeout(() => {
            if (!cancelled) navigate(next);
          }, 2000);
          return () => clearTimeout(t);
        }
        // ?activated=true pero user doc todavía no tiene subscriptionId
        // (race con el redirect): esperar 2s y re-leer una vez. Si todavía
        // nada, mostrar error suave.
        setDebugMsg('Activando tu suscripción…');
        await new Promise((r) => setTimeout(r, 2000));
        if (cancelled) return;
        const retry = await getDoc(userRef);
        const u2 = (retry.exists() && retry.data()) || {};
        if (u2.flowSubscriptionId && (u2.subscription_status === 'TRIAL' || u2.subscription_status === 'PAID')) {
          const next = u2.subscription_status === 'TRIAL' ? '/trial-activo' : nextRouteAfterSuccess(u2);
          setDebugMsg('¡Suscripción activa!');
          setTimeout(() => { if (!cancelled) navigate(next); }, 1500);
          return;
        }
        setError('La activación está demorando. Refresca esta página en unos segundos.');
        return;
      }

      // Caso edge: subscription ya creada (user tenía customerId+tarjeta antes).
      // Mismo routing: con Telegram → /dashboard, sin Telegram → /conectar-telegram.
      if (u.flowSubscriptionId && (u.subscription_status === 'TRIAL' || u.subscription_status === 'PAID')) {
        if (!cancelled) navigate(nextRouteAfterSuccess(u));
        return;
      }

      // F5 hotfix: pendingSubscription pero sin ?activated → user puede haber
      // cerrado tab antes de form.submit. Mostrar mensaje + retry a /paywall.
      // (Antes acá invocábamos callable flowCardRegistered, deprecated).
      const pending = u.pendingSubscription;
      if (pending && pending.registerToken) {
        setError('Tu suscripción quedó incompleta. Vuelve a empezar desde el paywall.');
        return;
      }

      // Fallback legacy: sin pendingSubscription ni señal one-shot → escuchar payments.
      startPaymentsListener();
    }

    init();

    return () => {
      cancelled = true;
      if (unsubLegacy) unsubLegacy();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, authLoading, navigate]);

  // Pantalla de error con botón retry.
  if (error) {
    return (
      <div className="mobile-root">
        <Helmet>
          <title>Activación con problema · Edge Elite</title>
        </Helmet>
        <section className="screen" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            No pudimos activar tu suscripción
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
            {error}
          </p>
          <button
            type="button"
            onClick={() => navigate('/paywall')}
            className="btn-primary accent"
            style={{ marginTop: 24, padding: '12px 24px' }}
          >
            Volver al paywall
          </button>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>
            Tu tarjeta no fue cobrada.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="mobile-root">
      <Helmet>
        <title>Verificando pago · Edge Elite</title>
      </Helmet>
      <section className="screen" style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          {debugMsg.includes('Activando') ? 'Activando suscripción…' : 'Verificando pago…'}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 8 }}>
          {debugMsg}
        </p>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 24 }}>
          No cierres esta ventana. Esto puede tardar unos segundos.
        </p>
      </section>
    </div>
  );
}
